import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Form, Input, Modal, Select } from 'antd';
import { safeUrlRule } from '@shared/utils/safeUrl';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { pickDefaultLanguage, useActiveLanguages } from '@features/languages/useLanguages';
import { POPUP_FREQUENCIES, type Popup, type PopupInput } from '../types';
import { fromRange, toRange, type RangeValue } from '../utils/dateRange';

const { RangePicker } = DatePicker;

interface FormValues {
  name: string;
  frequency: string;
  imageUrl?: string;
  title?: string;
  body?: string;
  ctaText?: string;
  ctaLink?: string;
  targetPages?: string;
  range?: RangeValue;
  languageId?: string;
}

interface PopupFormModalProps {
  open: boolean;
  editing: Popup | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: PopupInput) => void;
  onUpdate: (id: string, input: PopupInput) => void;
}

export function PopupFormModal({
  open,
  editing,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: PopupFormModalProps) {
  const { t } = useTranslation(['widgets', 'common', 'validation']);
  const td = t as unknown as (key: string) => string;
  const [form] = Form.useForm<FormValues>();
  const isEdit = editing !== null;
  const { data: languages = [] } = useActiveLanguages();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editing?.name ?? '',
      frequency: editing?.frequency || POPUP_FREQUENCIES[0],
      imageUrl: editing?.imageUrl ?? '',
      title: editing?.title ?? '',
      body: editing?.body ?? '',
      ctaText: editing?.ctaText ?? '',
      ctaLink: editing?.ctaLink ?? '',
      targetPages: editing?.targetPages ?? '',
      range: toRange(editing?.startDate, editing?.endDate) ?? undefined,
      languageId: editing ? undefined : pickDefaultLanguage(languages)?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, languages]);

  const onOk = () => {
    void (async () => {
      try {
        const v = await form.validateFields();
        const { startDate, endDate } = fromRange(v.range ?? null);
        const base: PopupInput = {
          name: v.name.trim(),
          frequency: v.frequency,
          imageUrl: v.imageUrl?.trim() || null,
          title: v.title?.trim() || null,
          body: v.body?.trim() || null,
          ctaText: v.ctaText?.trim() || null,
          ctaLink: v.ctaLink?.trim() || null,
          targetPages: v.targetPages?.trim() || null,
          startDate,
          endDate,
        };
        if (isEdit && editing) onUpdate(editing.id, base);
        else onCreate({ ...base, languageId: v.languageId ?? null });
      } catch {
        // inline validation
      }
    })();
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('popup.editTitle') : t('popup.createTitle')}
      onCancel={onCancel}
      onOk={onOk}
      okText={isEdit ? t('common:actions.save') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
      width={560}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item name="name" label={t('fields.name')} rules={[{ required: true, message: t('fields.nameRequired') }]}>
          <Input maxLength={120} placeholder={t('popup.namePlaceholder')} />
        </Form.Item>
        <Form.Item name="frequency" label={t('fields.frequency')} rules={[{ required: true }]}>
          <Select options={POPUP_FREQUENCIES.map((f) => ({ value: f, label: td(`frequency.${f}`) }))} />
        </Form.Item>
        <Form.Item name="targetPages" label={t('fields.targetPages')} help={t('fields.targetPagesHint')}>
          <Input placeholder={t('fields.targetPagesPlaceholder')} maxLength={1000} />
        </Form.Item>
        <Form.Item name="imageUrl" label={t('fields.imageUrl')} rules={[safeUrlRule(t('validation:safeUrl'))]}>
          <Input maxLength={500} placeholder={t('fields.imageUrlPlaceholder')} />
        </Form.Item>
        <Form.Item name="title" label={t('fields.title')}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="body" label={t('fields.body')}>
          <Input.TextArea rows={3} maxLength={1000} showCount />
        </Form.Item>
        <Form.Item name="ctaText" label={t('fields.ctaText')}>
          <Input maxLength={80} />
        </Form.Item>
        <Form.Item name="ctaLink" label={t('fields.ctaLink')} rules={[safeUrlRule(t('validation:safeUrl'))]}>
          <Input maxLength={500} placeholder={t('fields.ctaLinkPlaceholder')} />
        </Form.Item>
        <Form.Item name="range" label={t('fields.dateRange')} help={t('fields.dateRangeHint')}>
          <RangePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
        </Form.Item>
        {!isEdit ? (
          <Form.Item label={t('common:contentLanguage.label')} help={t('fields.languageHint')}>
            <Form.Item name="languageId" noStyle>
              <LanguagePickerField />
            </Form.Item>
          </Form.Item>
        ) : null}
      </Form>
    </Modal>
  );
}

function LanguagePickerField({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (v: string | undefined) => void;
}) {
  return <LanguagePicker value={value} onChange={(v) => onChange?.(v)} style={{ width: '100%' }} />;
}
